<template>

  <v-row class="mr-0">
    <v-col cols="6" sm="4">
      <v-text-field
          :label="$tt('collection-name-singular')"
          class="required unique"
          :model-value="value.name"
          :error-messages="errorMessages.name"
          @update:model-value="onInput('name', $event); validateName($event)"
          @blur="validateName()"
      />
    </v-col>
    <v-col cols="6" sm="4">
      <ontos-computed-input
          :label="$tt('collection-name-plural')"
          class="required unique"
          :value="value.pluralName"
          :error-messages="errorMessages.pluralName"
          @input="onInput('pluralName', $event); validatePluralName($event)"
          @blur="validatePluralName()"
          :source="value.name"
          :manual="manualPlural" @input:manual="manualPlural = $event"
          :compute="(text) => text? text + 's' : ''"
      />
    </v-col>
    <v-col>
      <ontos-computed-input
          :label="$tt('alias')"
          class="required unique ontos-code"
          :value="value.alias"
          @input="onInput('alias', $event); validateAlias($event)"
          @blur="validateAlias()"
          :error-messages="errorMessages.alias"
          :source="value.name"
          :manual="manualAlias" @input:manual="manualAlias = $event"
          :compute="$utils.aliasSuggestion"
          :computeOnInput="$utils.incompleteAliasSuggestion"
          :readonly="fixedAlias"
      />
    </v-col>
  </v-row>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import ComputedInput from "@/components/shared/ComputedInput.vue";
import Idea from "@/types/Idea";
import {namingError} from "@/utils/fieldValidation";





//type ValueProp = keyof typeof Value
type ValueProp = 'name' | 'pluralName' | 'alias'

@Component({emits: ['input'], components: {'ontos-computed-input': ComputedInput}})
class ClassInput extends Vue {
  @Prop({type: Object, required: true}) readonly value!: Idea
  @Prop({type: Boolean, default: false}) readonly fixedAlias!: boolean
  @Prop({type: Boolean, default: false}) readonly startWithAutoAlias!: boolean
  @Prop({type: Boolean, default: false}) readonly startWithAutoPlural!: boolean
  @Prop({type: Array, default: () => []}) readonly ideas!: Idea[]

  manualAlias = true
  manualPlural = true

  /**
   * No created, e não no inicializador do campo: o inicializador roda sobre a instância
   * crua da classe, onde as props ainda não existem. Lidas ali, as duas flags vinham de
   * `!undefined` — sempre true — e o alias deixava de ser sugerido automaticamente.
   */
  created() {
    this.manualAlias = !this.startWithAutoAlias
    this.manualPlural = !this.startWithAutoPlural
  }
  errorMessages = {
    name: "",
    pluralName: "",
    alias: "",
  }

  onInput(prop: ValueProp, value: any) {
    const changed: {[k:string]: any} = {}
    changed[prop] = value
    //Sem validar aqui: o template já chama o validateX de cada campo logo depois
    this.$emit('input', {...this.value, ...changed})
  }

  /** Confere o nome, o plural ou o alias entre as outras coleções — ver `namingError`. */
  checkNaming(prop: ValueProp, text: string) {
    this.errorMessages[prop] = namingError(text, prop, this.ideas, this.fixedAlias ? this.value.alias : undefined)
    return !this.errorMessages[prop]
  }

  validateName(text: string = this.value.name) {
    return this.checkNaming('name', text)
  }

  validatePluralName(text: string = this.value.pluralName) {
    return this.checkNaming('pluralName', text)
  }

  validateAlias(text: string = this.value.alias) {
    return this.checkNaming('alias', text)
  }

  /** Todos os campos, e não até o primeiro que falha: o usuário vê de uma vez o que corrigir. */
  validateModel() {
    return (Object.keys(this.errorMessages) as ValueProp[])
        .map(prop => this.checkNaming(prop, this.value[prop]))
        .every(valid => valid)
  }

  /** O servidor recusou o valor por já existir — a tela pode estar desatualizada. */
  notUnique(prop: ValueProp) {
    this.errorMessages[prop] = this.$tt('validation.already-used', {value: this.value[prop]}) as string
  }
}


export default toNative(ClassInput)
</script>
